import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mic, Square } from 'lucide-react';
import { toast } from 'sonner';
import { aiApi } from '../../services/api';
import { isNative } from '../../native/platform';
import { nativeDictationAvailable, startNativeDictation } from '../../native/speech';
import { canRecord, startRecording } from '../../lib/recordWav';

const Recognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
// Brave has the browser's speech API but no speech service behind it, so it always fails with
// "network"; Vivaldi too, which shows on the first try and is remembered here.
const SERVER_KEY = 'bb.dictation.server';
const NO_SERVICE = new Set(['network', 'service-not-allowed', 'language-not-supported']);
const MAX_SECONDS = 150; // a recording the server writes down (backend utils/dictation.py takes up to 180)

const serverRemembered = () => {
  try { return localStorage.getItem(SERVER_KEY) === '1'; } catch { return false; }
};
const rememberServer = () => {
  try { localStorage.setItem(SERVER_KEY, '1'); } catch { /* private mode */ }
};
const append = (text, said) => `${text ? `${text.replace(/\s+$/, '')} ` : ''}${said.trimStart()}`;

/**
 * Speak instead of typing: the browser's speech recognition, in the app's language;
 * in the iOS / Android app the phone's own (src/native/speech.js). Where the browser
 * has none that works (Brave, Vivaldi, Firefox) the recording is written down by the
 * server when it stops (paid from credits like every AI feature). What is said is
 * added to the text already in the box, so it can be corrected before sending.
 */
const DictateButton = ({ value, onChange, disabled }) => {
  const { t, i18n } = useTranslation();
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [nativeReady, setNativeReady] = useState(false);
  const rec = useRef(null);
  const recording = useRef(null);
  const limit = useRef(null);
  const stopNative = useRef(null);
  const base = useRef('');
  const latest = useRef(value);
  latest.current = value;
  const native = isNative();
  const browser = !!Recognition && !navigator.brave && !serverRemembered();

  useEffect(() => {
    if (native) nativeDictationAvailable().then(setNativeReady);
    return () => {
      rec.current?.abort();
      stopNative.current?.();
      recording.current?.cancel();
      clearTimeout(limit.current);
    };
  }, [native]);
  if (native ? !nativeReady : !Recognition && !canRecord()) return null;

  const toggleNative = async () => {
    if (listening) { await stopNative.current?.(); return; }
    base.current = value ? `${value.replace(/\s+$/, '')} ` : '';
    try {
      stopNative.current = await startNativeDictation(
        i18n.language,
        (said) => onChange(base.current + said.trimStart()),
        () => { setListening(false); stopNative.current = null; },
      );
      setListening(true);
    } catch {
      toast.error(t('bots.chat.dictate_failed'));
    }
  };

  const finishRecording = async () => {
    clearTimeout(limit.current);
    const current = recording.current;
    recording.current = null;
    if (!current) return;
    setListening(false);
    setTranscribing(true);
    try {
      const wav = await current.stop();
      const said = wav ? (await aiApi.transcribe(wav, i18n.language)).data.text : '';
      if (said) onChange(append(latest.current, said));
    } catch (err) {
      // Out of credits opens the upgrade dialog, the AI permission its own question.
      const status = err?.response?.status;
      if (status === 428) toast.error(err.response.data?.detail);
      else if (status !== 402) toast.error(t('bots.chat.dictate_transcribe_failed'));
    } finally {
      setTranscribing(false);
    }
  };

  const startServer = async () => {
    try {
      recording.current = await startRecording();
      setListening(true);
      limit.current = setTimeout(finishRecording, MAX_SECONDS * 1000);
    } catch {
      setListening(false);
      toast.error(t('bots.chat.dictate_failed'));
    }
  };

  const toggle = () => {
    if (transcribing) return;
    if (native) { toggleNative(); return; }
    if (recording.current) { finishRecording(); return; }
    if (!browser) { startServer(); return; }
    if (listening) { rec.current?.stop(); return; }
    const r = new Recognition();
    r.lang = i18n.language;
    r.interimResults = true;
    r.continuous = true;
    base.current = value ? `${value.replace(/\s+$/, '')} ` : '';
    let fellBack = false;
    r.onresult = (e) => {
      let said = '';
      for (let i = 0; i < e.results.length; i += 1) said += e.results[i][0].transcript;
      onChange(base.current + said.trimStart());
    };
    r.onerror = (e) => {
      if (NO_SERVICE.has(e.error) && canRecord()) {  // no speech service: the server writes it down
        fellBack = true;
        rememberServer();
        startServer();
        return;
      }
      if (e.error !== 'aborted' && e.error !== 'no-speech') toast.error(t('bots.chat.dictate_failed'));
    };
    r.onend = () => { if (!fellBack) setListening(false); };
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      toast.error(t('bots.chat.dictate_failed'));
    }
  };

  const label = t(transcribing ? 'bots.chat.dictate_transcribing' : listening ? 'bots.chat.dictate_stop' : 'bots.chat.dictate');
  return (
    <button type="button" onClick={toggle} disabled={disabled} data-testid="bot-dictate"
      title={label} aria-label={label}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full disabled:opacity-30 ${listening ? 'bg-red-500/15 text-red-500 animate-pulse' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}>
      {transcribing ? <Loader2 className="w-4 h-4 animate-spin" />
        : listening ? <Square className="w-3.5 h-3.5 fill-current" /> : <Mic className="w-4 h-4" />}
    </button>
  );
};

export default DictateButton;
